import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import JourneyQuoteProductAccordion from '~/components/PersonalCoverage/JourneyQuote2026/JourneyQuoteProductAccordion';
import JourneyQuoteSummaryColumn, {
  type JourneyQuoteSubscriptionRow
} from '~/components/PersonalCoverage/JourneyQuote2026/JourneyQuoteSummaryColumn';
import ModalTermOfContract from '~/modals/ModalTermOfContract';
import { RENEWAL_APP_IDS } from '../renewalAppIds';
import type { RenewalCoverageItem } from '../renewalCoverage';
import { formatRenewalCurrency } from '../renewalReview';

export type RenewalReviewOffer = {
  coverageItems: RenewalCoverageItem[];
  upsellItems: RenewalCoverageItem[];
  selectedIds: number[];
  summaryRows: JourneyQuoteSubscriptionRow[];
  totalMonthly: number;
  serviceCharge: number;
  recalculating: boolean;
  renewing: boolean;
  organizationId: string;
  onToggleUpsell: (productId: number) => void;
  onRenew: () => void;
};

type Props = RenewalReviewOffer & {
  /** Um card de dados em edição bloqueia a renovação. */
  editingCard: boolean;
};

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <Box>
      <Typography component="h2" sx={{ color: 'text.primary' }} variant="h5">
        {title}
      </Typography>
      <Typography sx={(theme) => ({ color: theme.customColors.textPlaceholder })} variant="subtitle1">
        {subtitle}
      </Typography>
    </Box>
  );
}

/**
 * Coberturas, upsell e resumo da renovação (Figma 2086:7579 e 2088:8571). Reaproveita a sanfona e o
 * resumo da cotação 2026; no mobile o resumo e o botão vêm abaixo das coberturas.
 */
export default function RenewalOfferSection({
  coverageItems,
  upsellItems,
  selectedIds,
  summaryRows,
  totalMonthly,
  serviceCharge,
  recalculating,
  renewing,
  organizationId,
  onToggleUpsell,
  onRenew,
  editingCard
}: Props) {
  const [acceptedTracking, setAcceptedTracking] = useState(false);
  const [acceptedUsage, setAcceptedUsage] = useState(false);

  const accordion = (item: RenewalCoverageItem) => (
    <JourneyQuoteProductAccordion
      clubType={undefined}
      freeLabel="Sem custo"
      isFree={item.product_price === 0}
      isSelected={item.required || selectedIds.includes(Number(item.product_id))}
      key={item.product_id}
      onToggle={() => onToggleUpsell(Number(item.product_id))}
      quote={{ ...item, required: item.required || recalculating }}
      showIncludedBadge={false}
    />
  );

  const canRenew = acceptedTracking && acceptedUsage && !editingCard && !recalculating && !renewing;

  return (
    <Box
      data-app-id={RENEWAL_APP_IDS.offer}
      sx={{
        alignItems: 'start',
        display: 'grid',
        gap: '24px',
        gridTemplateColumns: { md: 'minmax(0, 1fr) minmax(0, 478px)', xs: 'minmax(0, 1fr)' }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', minWidth: 0 }}>
        <SectionTitle subtitle="Tudo o que continua valendo quando você renovar." title="Coberturas do seu contrato" />
        <Box data-app-id={RENEWAL_APP_IDS.coverages}>{coverageItems.map(accordion)}</Box>

        {upsellItems.length > 0 ? (
          <>
            <SectionTitle
              subtitle="Condições especiais para quem renova. Adicione agora e pague junto na mesma mensalidade."
              title="Aproveite para reforçar sua proteção"
            />
            <Box data-app-id={RENEWAL_APP_IDS.upsell}>{upsellItems.map(accordion)}</Box>
          </>
        ) : null}
      </Box>

      <Box data-app-id={RENEWAL_APP_IDS.summary} data-testid="renewal-summary" sx={{ minWidth: 0 }}>
        <JourneyQuoteSummaryColumn
          advanceLabel="Renovar meu contrato"
          advancing={renewing}
          disableAdvance={!canRenew}
          membershipFee={0}
          onAdvance={onRenew}
          productsLoading={recalculating}
          securityMessage="Seus dados estão protegidos e serão usados apenas para a renovação."
          serviceChargeFormatted={formatRenewalCurrency(serviceCharge)}
          showOperationalRow={serviceCharge > 0}
          subscriptionRows={summaryRows}
          termsSlot={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <ModalTermOfContract
                checkedTermOfContract={acceptedTracking}
                linkLabel="Termo de rastreamento"
                organizationId={organizationId}
                setCheckedTermOfContract={setAcceptedTracking}
                termType="TrackingService"
              />
              <ModalTermOfContract
                checkedTermOfContract={acceptedUsage}
                linkLabel="Termo de uso"
                organizationId={organizationId}
                setCheckedTermOfContract={setAcceptedUsage}
              />
            </Box>
          }
          totalMonthlyNumber={totalMonthly}
        />
      </Box>
    </Box>
  );
}
